import { Currency, getCurrencySymbol } from '@/constants/Currency';
import { DEFAULT_DECIMAL_PLACES } from '@/constants/NumberConstants';
import { displayStaticDatum, preciseValue } from '@/filters';
import type { RiskDashboardFieldConfig } from '@/types/RiskDashboardFieldConfig';
import { UnitConstants as DisplayUnitConstants } from '@/types/analytics/ConstituentRisk';
import { UnitConstants as ReportFieldUnitConstants } from '@/types/admin/reportFields';

// Hardcoded columns that must not use thousand separators (IDs stay copy-friendly).
export const COLUMNS_WITHOUT_SEPARATORS = new Set(['Security ID', 'Datafeed Portfolio ID']);

// Percentage report fields still stored and returned as 0–100, so they are not multiplied by 100.
export const PERCENTAGE_FIELDS_WITHOUT_SCALING = new Set(['Volatility', 'Duration Times Spread (DTS)']);

export interface FormatValueWithFieldConfigOptions {
  currency?: string;
  column?: string;
  displayUnit?: DisplayUnitConstants;
  isStaticValue?: boolean;
}

function isCurrencyDisplayUnit(displayUnit?: DisplayUnitConstants): boolean {
  return displayUnit === DisplayUnitConstants.CURRENCY;
}

function isPercentDisplayUnit(displayUnit?: DisplayUnitConstants): boolean {
  return displayUnit === DisplayUnitConstants.PERCENT_VALUE || displayUnit === DisplayUnitConstants.PERCENT_SELECTION;
}

function shouldUseSeparators(column?: string): boolean {
  if (column && COLUMNS_WITHOUT_SEPARATORS.has(column)) {
    return false;
  }

  return true;
}

function scalePercentageReportFieldValue(value: number, column?: string): number {
  if (column != null && PERCENTAGE_FIELDS_WITHOUT_SCALING.has(column)) {
    return value;
  }

  return preciseValue(value * 100);
}

function formatNumericValue(
  value: number,
  decimalPlaces: number,
  { percent = false, column }: { percent?: boolean; column?: string } = {},
): string {
  const useSeparators = shouldUseSeparators(column);

  return displayStaticDatum(value, {
    numDecimalPoints: decimalPlaces,
    percent,
    useSeparators,
  });
}

function formatCurrencyValue(value: number, decimalPlaces: number, currency: string, column?: string): string {
  const useSeparators = shouldUseSeparators(column);

  const formattedValue = useSeparators
    ? value.toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      })
    : value.toFixed(decimalPlaces);

  return `${getCurrencySymbol(currency as Currency)} ${formattedValue}`;
}

export function resolveCurrencyForFieldConfig(
  config: RiskDashboardFieldConfig,
  rowCurrency: string | null | undefined,
  reportCurrency: Currency,
): Currency {
  if (config.unit === ReportFieldUnitConstants.HOLDING_CCY) {
    if (typeof rowCurrency === 'string' && rowCurrency !== '') {
      return rowCurrency as Currency;
    }

    return reportCurrency;
  }

  if (config.unit === ReportFieldUnitConstants.REPORT_CCY) {
    return reportCurrency;
  }

  return reportCurrency;
}

function formatCurrencyFieldValue(
  value: number,
  decimalPlaces: number,
  options: FormatValueWithFieldConfigOptions,
): string {
  const { column, currency, displayUnit, isStaticValue = false } = options;

  if (isStaticValue || isCurrencyDisplayUnit(displayUnit)) {
    return formatCurrencyValue(value, decimalPlaces, currency ?? Currency.USD, column);
  }

  if (isPercentDisplayUnit(displayUnit)) {
    return formatNumericValue(value, decimalPlaces, { percent: true, column });
  }

  return formatNumericValue(value, decimalPlaces, { column });
}

export function formatValueWithFieldConfig(
  value: number,
  config: RiskDashboardFieldConfig,
  options: FormatValueWithFieldConfigOptions = {},
): string | null {
  const decimalPlaces = config.decimalPlaces ?? DEFAULT_DECIMAL_PLACES;
  const { column, displayUnit, isStaticValue = false } = options;
  const shouldShowPercent = isPercentDisplayUnit(displayUnit) && !isStaticValue;

  if (config.unit === ReportFieldUnitConstants.PERCENTAGE) {
    return formatNumericValue(scalePercentageReportFieldValue(value, column), decimalPlaces, {
      percent: true,
      column,
    });
  }

  if (config.unit === ReportFieldUnitConstants.REPORT_CCY || config.unit === ReportFieldUnitConstants.HOLDING_CCY) {
    return formatCurrencyFieldValue(value, decimalPlaces, options);
  }

  if (config.unit != null) {
    return null;
  }

  return formatNumericValue(value, decimalPlaces, {
    percent: shouldShowPercent,
    column,
  });
}
